import { type FieldNoteMeta, noteSection } from "../content/field-notes.js"

/**
 * One owner for everything a crawler reads off a page: the site constants, the
 * head tags every route emits, and the schema.org JSON-LD graph. Every route's
 * `head()` composes from here, so the entity facts are written once.
 *
 * FACTS ONLY. Everything in the Person node is a checked fact about Laith. No
 * awards, no contact details, no handles this module cannot verify (X and
 * YouTube are deliberately absent).
 */

import { BIO_INTRO, BIO_WORK } from "../content/profile.js"

export const SITE = {
  origin: "https://laithalsaadoon.com",
  name: "Laith Al-Saadoon",
  personId: "https://laithalsaadoon.com/#laith",
  webSiteId: "https://laithalsaadoon.com/#website",
  ogImagePath: "/og.png",
  ogImageAlt: "The laithalsaadoon.com masthead: Laith Al-Saadoon, Principal AI Engineer at AWS",
  locale: "en_US",
  language: "en"
} as const

export const PERSON = {
  name: "Laith Al-Saadoon",
  url: "https://laithalsaadoon.com",
  jobTitle: "Principal AI Engineer",
  employer: { name: "Amazon Web Services", url: "https://aws.amazon.com" },
  home: { locality: "Houston", region: "TX", country: "US" },
  sameAs: [
    "https://github.com/laithalsaadoon",
    "https://www.linkedin.com/in/laithalsaadoon",
    "https://builder.aws.com/community/@lalsaado"
  ],
  description: [BIO_INTRO, BIO_WORK]
} as const

/** Hand-written descriptions for the three fixed pages, 150-160 characters each. */
export const DESCRIPTIONS = {
  home: "Laith Al-Saadoon is a Principal AI Engineer at AWS in Houston, Texas. Frontier Field Notes on the autonomous work horizon, agent sandboxes, and evals.",
  about:
    "Who runs laithalsaadoon.com: Laith Al-Saadoon, Principal AI Engineer at Amazon Web Services, on the autonomous work horizon, agent infrastructure, and evals.",
  notes:
    "Frontier Field Notes by Laith Al-Saadoon: long-form essays and short side quests on the autonomous work horizon, agent infrastructure, evals, and coding agents."
} as const

/** The longest a `<title>` may run before the site name is dropped from it. */
export const TITLE_MAX = 60
/** The description window: trim at a word boundary above the ceiling. */
export const DESCRIPTION_MIN = 150
export const DESCRIPTION_MAX = 160

// ---------------------------------------------------------------------------
// JSON-LD types: plain JSON, so a node can never carry a function or a Date.
// ---------------------------------------------------------------------------

export type JsonValue =
  | string
  | number
  | boolean
  | null
  | JsonValue[]
  | { [key: string]: JsonValue }
export type JsonLd = { [key: string]: JsonValue }

// ---------------------------------------------------------------------------
// URLs
// ---------------------------------------------------------------------------

/** The canonical URL of a path: the origin plus the path, with "/" for the home page. */
export const canonicalUrl = (path: string): string =>
  path === "/" ? `${SITE.origin}/` : `${SITE.origin}${path.replace(/\/+$/, "")}`

export const ogImageUrl = (): string => `${SITE.origin}${SITE.ogImagePath}`

export const notePath = (slug: string): string => `/notes/${slug}`

// ---------------------------------------------------------------------------
// Text helpers
// ---------------------------------------------------------------------------

/**
 * `<title>` text: the page title with the site name appended when the pair fits
 * in TITLE_MAX characters, the bare page title otherwise. A title that already
 * names the site (the home page, the About page) comes back unchanged.
 */
export function pageTitle(title: string): string {
  if (title.includes(SITE.name)) return title
  const joined = `${title} · ${SITE.name}`
  return joined.length <= TITLE_MAX ? joined : title
}

/**
 * Cut `text` to at most `max` characters at a word boundary. A cut text ends in
 * an ellipsis (one character) so it never reads as a complete sentence it is not;
 * trailing punctuation before the cut is dropped so the result is not ",…".
 */
export function trimAtWord(text: string, max: number): string {
  const clean = text.replace(/\s+/g, " ").trim()
  if (clean.length <= max) return clean
  const window = clean.slice(0, max - 1)
  // A word ending exactly at the window's edge is whole and stays.
  const cut = clean[max - 1] === " " ? window.length : window.lastIndexOf(" ")
  const head = (cut > 0 ? window.slice(0, cut) : window).replace(/[\s,;:.]+$/, "")
  return `${head}…`
}

/**
 * A meta description derived from a summary: the summary itself, plus a short
 * attribution when there is room for it under DESCRIPTION_MAX, trimmed at a word
 * boundary when the summary alone is already too long.
 */
export function describe(summary: string, attribution: string): string {
  const base = summary.replace(/\s+/g, " ").trim()
  const withAttribution = `${base} ${attribution}`
  if (base.length < DESCRIPTION_MIN && withAttribution.length <= DESCRIPTION_MAX) {
    return withAttribution
  }
  // A whole sentence beats a trimmed one when a sentence end falls inside the window.
  const window = base.slice(0, DESCRIPTION_MAX)
  const sentenceEnd = Math.max(
    window.lastIndexOf(". "),
    window.lastIndexOf(".") === window.length - 1 ? window.length - 1 : -1
  )
  if (sentenceEnd >= 100) return window.slice(0, sentenceEnd + 1)
  return trimAtWord(base, DESCRIPTION_MAX)
}

export const noteDescription = (note: FieldNoteMeta): string =>
  describe(
    note.abstract,
    note.kind === "sidequest"
      ? `A side quest by ${PERSON.name}.`
      : `A field note by ${PERSON.name}.`
  )

// ---------------------------------------------------------------------------
// Head tags
// ---------------------------------------------------------------------------

export type MetaTag =
  | { readonly title: string }
  | { readonly name: string; readonly content: string }
  | { readonly property: string; readonly content: string }

/** One `<script type="application/ld+json">` with its serialised node. */
export interface ScriptTag {
  readonly type: "application/ld+json"
  readonly children: string
}

export interface LinkTag {
  readonly rel: string
  readonly href: string
  readonly type?: string
}

/** Mutable arrays on purpose: this is the shape TanStack's `head()` option accepts. */
export interface PageHead {
  meta: MetaTag[]
  links: LinkTag[]
  scripts: ScriptTag[]
}

/**
 * JSON-LD as script text. `<` becomes `\u003c` (still valid JSON) so no value can
 * close the script element early; the tag is written with its text verbatim.
 */
export const jsonLdScript = (node: JsonLd): ScriptTag => ({
  type: "application/ld+json",
  children: JSON.stringify(node).replace(/</g, "\\u003c")
})

export interface PageMetaInput {
  readonly title: string
  readonly description: string
  /** The route path, "/" for home. The canonical and og:url derive from it. */
  readonly path: string
  readonly type?: "website" | "article"
  /** Absolute image URL; defaults to the site card. */
  readonly image?: string
  readonly imageAlt?: string
  /** ISO 8601 publication date, emitted as article:published_time for articles. */
  readonly published?: string
  /** ISO 8601 modification date, emitted as article:modified_time for articles. */
  readonly modified?: string
  readonly jsonLd?: ReadonlyArray<JsonLd>
}

/**
 * Every head tag a page carries: title, description, robots, canonical, Open
 * Graph, Twitter Card, and the page's JSON-LD nodes as head scripts. The order
 * is the order the tags land in the document.
 */
export function pageMeta(input: PageMetaInput): PageHead {
  const title = pageTitle(input.title)
  const url = canonicalUrl(input.path)
  const image = input.image ?? ogImageUrl()
  const type = input.type ?? "website"
  const meta: MetaTag[] = [
    { title },
    { name: "description", content: input.description },
    { name: "robots", content: "index,follow" },
    { name: "author", content: PERSON.name },
    { property: "og:type", content: type },
    { property: "og:title", content: title },
    { property: "og:description", content: input.description },
    { property: "og:url", content: url },
    { property: "og:site_name", content: SITE.name },
    { property: "og:locale", content: SITE.locale },
    { property: "og:image", content: image },
    { property: "og:image:width", content: "1200" },
    { property: "og:image:height", content: "630" },
    { property: "og:image:alt", content: input.imageAlt ?? SITE.ogImageAlt },
    { name: "twitter:card", content: "summary_large_image" },
    { name: "twitter:title", content: title },
    { name: "twitter:description", content: input.description },
    { name: "twitter:image", content: image },
    { name: "twitter:image:alt", content: input.imageAlt ?? SITE.ogImageAlt }
  ]
  if (type === "article") {
    if (input.published !== undefined) {
      meta.push({ property: "article:published_time", content: input.published })
    }
    if (input.modified !== undefined) {
      meta.push({ property: "article:modified_time", content: input.modified })
    }
    meta.push({ property: "article:author", content: PERSON.url })
  }
  return {
    meta,
    links: [
      { rel: "canonical", href: url },
      { rel: "alternate", href: "/feed.xml", type: "application/rss+xml" },
      ...(type === "article"
        ? [{ rel: "alternate", href: `${input.path}.md`, type: "text/markdown" }]
        : [])
    ],
    scripts: (input.jsonLd ?? []).map(jsonLdScript)
  }
}

// ---------------------------------------------------------------------------
// JSON-LD nodes
// ---------------------------------------------------------------------------

const CONTEXT = "https://schema.org"

/** The short Person reference every article points its author and publisher at. */
export const personRef = (): JsonLd => ({
  "@type": "Person",
  "@id": SITE.personId,
  name: PERSON.name,
  url: PERSON.url
})

export function personJsonLd(): JsonLd {
  return {
    "@context": CONTEXT,
    "@type": "Person",
    "@id": SITE.personId,
    name: PERSON.name,
    url: PERSON.url,
    jobTitle: PERSON.jobTitle,
    description: PERSON.description.join(" "),
    worksFor: {
      "@type": "Organization",
      name: PERSON.employer.name,
      url: PERSON.employer.url
    },
    homeLocation: {
      "@type": "Place",
      address: {
        "@type": "PostalAddress",
        addressLocality: PERSON.home.locality,
        addressRegion: PERSON.home.region,
        addressCountry: PERSON.home.country
      }
    },
    sameAs: [...PERSON.sameAs],
    mainEntityOfPage: canonicalUrl("/about")
  }
}

export function webSiteJsonLd(): JsonLd {
  return {
    "@context": CONTEXT,
    "@type": "WebSite",
    "@id": SITE.webSiteId,
    name: SITE.name,
    url: canonicalUrl("/"),
    inLanguage: SITE.language,
    author: personRef(),
    publisher: personRef()
  }
}

export interface Crumb {
  readonly name: string
  readonly path: string
}

export function breadcrumbJsonLd(crumbs: ReadonlyArray<Crumb>): JsonLd {
  return {
    "@context": CONTEXT,
    "@type": "BreadcrumbList",
    itemListElement: crumbs.map((crumb, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: crumb.name,
      item: canonicalUrl(crumb.path)
    }))
  }
}

export interface ListEntry {
  readonly name: string
  readonly path: string
}

export function itemListJsonLd(name: string, entries: ReadonlyArray<ListEntry>): JsonLd {
  return {
    "@context": CONTEXT,
    "@type": "ItemList",
    name,
    numberOfItems: entries.length,
    itemListElement: entries.map((entry, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: entry.name,
      url: canonicalUrl(entry.path)
    }))
  }
}

/**
 * The Article node uses the original publication date and, when supplied,
 * the editorial revision date shown on the page.
 */
export function noteJsonLd(note: FieldNoteMeta, image: string = ogImageUrl()): JsonLd {
  const url = canonicalUrl(notePath(note.slug))
  return {
    "@context": CONTEXT,
    "@type": "Article",
    "@id": `${url}#article`,
    mainEntityOfPage: url,
    url,
    headline: note.title,
    description: note.abstract,
    datePublished: note.dateSort,
    dateModified: note.modified ?? note.dateSort,
    author: personRef(),
    publisher: personRef(),
    image,
    keywords: note.tags.join(", "),
    articleSection: noteSection(note),
    inLanguage: SITE.language,
    isPartOf: { "@id": SITE.webSiteId }
  }
}

// ---------------------------------------------------------------------------
// Per-route heads
// ---------------------------------------------------------------------------

export const HOME_CRUMB: Crumb = { name: "Home", path: "/" }
export const NOTES_CRUMB: Crumb = { name: "Frontier Field Notes", path: "/notes" }

export const homeHead = (): PageHead =>
  pageMeta({
    title: `${SITE.name} · AI agents, sandboxes and evals`,
    description: DESCRIPTIONS.home,
    path: "/",
    jsonLd: [personJsonLd(), webSiteJsonLd()]
  })

export const aboutHead = (): PageHead =>
  pageMeta({
    title: `About ${SITE.name}`,
    description: DESCRIPTIONS.about,
    path: "/about",
    jsonLd: [personJsonLd(), breadcrumbJsonLd([HOME_CRUMB, { name: "About", path: "/about" }])]
  })

const listEntries = (notes: ReadonlyArray<FieldNoteMeta>): ListEntry[] =>
  notes.map((note) => ({ name: note.title, path: notePath(note.slug) }))

/**
 * The hub's head: one ItemList per group, in the order the page shows them (the
 * long-form Field Notes, then the Side Quests), so a crawler sees the same split
 * a reader does.
 */
export const notesHead = (
  essays: ReadonlyArray<FieldNoteMeta>,
  sideQuests: ReadonlyArray<FieldNoteMeta>
): PageHead =>
  pageMeta({
    title: "Frontier Field Notes",
    description: DESCRIPTIONS.notes,
    path: "/notes",
    jsonLd: [
      itemListJsonLd("Field Notes", listEntries(essays)),
      itemListJsonLd("Side Quests", listEntries(sideQuests)),
      breadcrumbJsonLd([HOME_CRUMB, NOTES_CRUMB])
    ]
  })

export const noteHead = (note: FieldNoteMeta): PageHead =>
  pageMeta({
    title: note.title,
    description: noteDescription(note),
    path: notePath(note.slug),
    type: "article",
    published: note.dateSort,
    modified: note.modified ?? note.dateSort,
    image: `${SITE.origin}/og/${note.slug}.png`,
    imageAlt: `${note.title} · Frontier Field Notes by ${PERSON.name}`,
    jsonLd: [
      noteJsonLd(note, `${SITE.origin}/og/${note.slug}.png`),
      breadcrumbJsonLd([HOME_CRUMB, NOTES_CRUMB, { name: note.title, path: notePath(note.slug) }])
    ]
  })
